import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type NormalizerFunction<T = string> = (value: string | null) => T | undefined;

type QueryParamConfig<T = string> = {
  normalize?: NormalizerFunction<T>;
  defaultValue?: T;
};

type QueryParamSchema = {
  [key: string]: QueryParamConfig<string | number | boolean>;
};

type InferParamType<T> = T extends QueryParamConfig<infer U> ? U : string;

type QueryParamValues<TSchema extends QueryParamSchema> = {
  [K in keyof TSchema]: InferParamType<TSchema[K]>;
};

type UseQueryParamsResult<TSchema extends QueryParamSchema> = {
  /** The parsed and normalized query parameters based on the provided schema */
  params: QueryParamValues<TSchema>;
  /** current, unparsed state of the URL search parameters. Used for navigation history */
  searchParams: URLSearchParams;
  setParam: <K extends keyof TSchema>(key: K, value: string | null) => void;
  setParams: (updates: Partial<Record<keyof TSchema, string | null>>) => void;
  deleteParam: <K extends keyof TSchema>(key: K) => void;
  deleteParams: (keys: Array<keyof TSchema>) => void;
  clearParams: () => void;
  getRawParam: (key: keyof TSchema) => string | null;
};

export function useQueryParams<TSchema extends QueryParamSchema>(schema: TSchema): UseQueryParamsResult<TSchema> {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const result: Record<string, string | number | boolean> = {};

    for (const key in schema) {
      const config = schema[key];
      if (!config) continue;

      const rawValue = searchParams.get(key);
      let finalValue: string | number | boolean;

      if (config.normalize) {
        const normalized = config.normalize(rawValue);
        finalValue = normalized !== undefined ? normalized : (config.defaultValue ?? '');
      } else {
        finalValue = rawValue ?? config.defaultValue ?? '';
      }

      result[key] = finalValue;
    }

    return result as QueryParamValues<TSchema>;
  }, [searchParams, schema]);

  const setParam = useCallback(
    <K extends keyof TSchema>(key: K, value: string | null) => {
      setSearchParams((prev) => {
        if (value === null || value === '') {
          prev.delete(String(key));
        } else {
          prev.set(String(key), value);
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  const setParams = useCallback(
    (updates: Partial<Record<keyof TSchema, string | null>>) => {
      setSearchParams((prev) => {
        for (const key in updates) {
          const value = updates[key];
          if (value === null || value === '' || value === undefined) {
            prev.delete(key);
          } else {
            prev.set(key, value);
          }
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  const deleteParam = useCallback(
    <K extends keyof TSchema>(key: K) => {
      setSearchParams((prev) => {
        prev.delete(String(key));
        return prev;
      });
    },
    [setSearchParams]
  );

  const deleteParams = useCallback(
    (keys: Array<keyof TSchema>) => {
      setSearchParams((prev) => {
        keys.forEach((key) => prev.delete(String(key)));
        return prev;
      });
    },
    [setSearchParams]
  );

  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const getRawParam = useCallback(
    (key: keyof TSchema) => {
      return searchParams.get(String(key));
    },
    [searchParams]
  );

  return {
    params,
    searchParams,
    setParam,
    setParams,
    deleteParam,
    deleteParams,
    clearParams,
    getRawParam
  };
}

export const normalizers = {
  string: (value: string | null): string => value || '',

  nonNegativeInteger: (value: string | null): number | undefined => {
    const num = parseInt(value || '', 10);
    return !isNaN(num) && num >= 0 ? num : undefined;
  },

  boolean: (value: string | null): boolean | undefined => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  },

  enum: <T extends string>(allowedValues: readonly T[]): NormalizerFunction<T> => {
    return (value: string | null): T | undefined => {
      if (!value) return undefined;
      return allowedValues.some((allowed) => allowed === value) ? (value as T) : undefined;
    };
  },

  isoDate: (value: string | null): string | undefined => {
    if (!value) return undefined;
    const date = new Date(value);
    return !isNaN(date.getTime()) ? date.toISOString() : undefined;
  }
};
